import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PITCH_MODES, OBJECT_TYPES, FIELD_COLORS, PLAYER_SIZES, PLAYER_SIZE_KEYS, DEFAULT_PLAYER_SIZE, isPlayerLikeType } from "@/constants/pitchObjects";
import { X, Minus, ArrowUpToLine, ArrowDownToLine, Plus, Check } from "lucide-react";

const COLORS = [
  { key: "black", hex: "#0F172A" },
  { key: "white", hex: "#FFFFFF" },
  { key: "red", hex: "#DC2626" },
  { key: "blue", hex: "#2563EB" },
  { key: "orange", hex: "#F97316" },
  { key: "pink", hex: "#EC4899" },
];

const Section = ({ title, children }) => (
  <section className="flex flex-col gap-3">
    <h3 className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
      {title}
    </h3>
    {children}
  </section>
);

const PLAYER_COLOR_SWATCHES = [
  "#0F172A",
  "#DC2626",
  "#F59E0B",
  "#10B981",
  "#3B82F6",
  "#A855F7",
  "#FFFFFF",
  "#64748B",
];

const PlayerColorSwatches = ({ activeColor, onPick, testIdPrefix = "player-color" }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-swatches`}>
    {PLAYER_COLOR_SWATCHES.map((c) => {
      const active = !!activeColor && activeColor.toLowerCase() === c.toLowerCase();
      return (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          data-testid={`${testIdPrefix}-${c.replace('#', '').toLowerCase()}`}
          aria-label={`Set player colour to ${c}`}
          aria-pressed={active}
          className={
            "w-6 h-6 rounded-full transition-all border " +
            (active
              ? "ring-2 ring-slate-900 ring-offset-2 ring-offset-white border-slate-900"
              : "border-slate-200 hover:border-slate-400")
          }
          style={{ backgroundColor: c }}
        />
      );
    })}
  </div>
);

const StyleControls = ({ value, onChange, testIdPrefix }) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-slate-700">Color</span>
        <div className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-colors`}>
          {COLORS.map((c) => {
            const active = value.color?.toLowerCase() === c.hex.toLowerCase();
            return (
              <button
                type="button"
                key={c.key}
                onClick={() => onChange({ color: c.hex })}
                data-testid={`${testIdPrefix}-color-${c.key}`}
                aria-label={`${c.key} color`}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  active
                    ? "border-slate-900 ring-2 ring-slate-900 ring-offset-1"
                    : "border-slate-300 hover:border-slate-500"
                }`}
                style={{ backgroundColor: c.hex }}
              />
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">Stroke</span>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
          <button
            type="button"
            onClick={() => onChange({ dashed: false })}
            data-testid={`${testIdPrefix}-stroke-solid`}
            className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
              !value.dashed
                ? "bg-slate-900 text-white"
                : "bg-transparent text-slate-600 hover:bg-slate-100"
            }`}
          >
            Solid
          </button>
          <button
            type="button"
            onClick={() => onChange({ dashed: true })}
            data-testid={`${testIdPrefix}-stroke-dashed`}
            className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
              value.dashed
                ? "bg-slate-900 text-white"
                : "bg-transparent text-slate-600 hover:bg-slate-100"
            }`}
          >
            Dashed
          </button>
        </div>
      </div>
    </div>
  );
};

const genCoachingPointId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const CoachingPointRow = ({ point, onRemove }) => (  <li
    className="flex items-start gap-2 text-xs text-slate-700 leading-snug"
    data-testid={`coaching-point-row-${point.id}`}
  >
    <span aria-hidden className="mt-1.5 w-1 h-1 rounded-full bg-slate-500 shrink-0" />
    <span className="flex-1 break-words">{point.text}</span>
    <button
      type="button"
      onClick={onRemove}
      data-testid={`coaching-point-remove-${point.id}`}
      aria-label="Remove coaching point"
      className="w-5 h-5 shrink-0 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
  </li>
);

const CoachingPointsSection = ({ points, onChange }) => {
  // Local UI state for the "add" flow. `mode` is null when the section is
  // idle, "picking-type" while the coach chooses Offensive/Defensive, and
  // "typing" once a type is chosen and the text input is visible.
  const [mode, setMode] = useState(null);
  const [draftType, setDraftType] = useState(null);
  const [draftText, setDraftText] = useState("");

  const startAdd = () => {
    setMode("picking-type");
    setDraftType(null);
    setDraftText("");
  };
  const cancelAdd = () => {
    setMode(null);
    setDraftType(null);
    setDraftText("");
  };
  const pickType = (t) => {
    setDraftType(t);
    setMode("typing");
  };
  const confirmAdd = () => {
    const text = draftText.trim();
    if (!text || !draftType) return;
    onChange([
      ...(points || []),
      { id: genCoachingPointId(), type: draftType, text },
    ]);
    cancelAdd();
  };
  const removePoint = (id) => {
    onChange((points || []).filter((p) => p.id !== id));
  };

  const offensive = (points || []).filter((p) => p.type === "offensive");
  const defensive = (points || []).filter((p) => p.type === "defensive");

  return (
    <section className="flex flex-col gap-3" data-testid="coaching-points-section">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
          Coaching Points
        </h3>
        <button
          type="button"
          onClick={startAdd}
          data-testid="coaching-point-add-btn"
          aria-label="Add coaching point"
          disabled={mode !== null}
          className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {mode === "picking-type" && (
        <div
          className="rounded-md border border-slate-200 bg-slate-50 p-3 flex flex-col gap-2"
          data-testid="coaching-point-type-picker"
        >
          <p className="text-[11px] text-slate-500">Choose a type</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => pickType("offensive")}
              data-testid="coaching-point-type-offensive"
              className="text-[10px] font-semibold uppercase tracking-wide py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Offensive
            </button>
            <button
              type="button"
              onClick={() => pickType("defensive")}
              data-testid="coaching-point-type-defensive"
              className="text-[10px] font-semibold uppercase tracking-wide py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Defensive
            </button>
          </div>
          <button
            type="button"
            onClick={cancelAdd}
            data-testid="coaching-point-cancel-btn"
            className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800 self-end"
          >
            Cancel
          </button>
        </div>
      )}

      {mode === "typing" && (
        <div
          className="rounded-md border border-slate-200 bg-slate-50 p-3 flex flex-col gap-2"
          data-testid="coaching-point-text-form"
        >
          <div className="flex items-center justify-between">
            <span
              className={
                "text-[9px] tracking-[0.2em] uppercase font-bold " +
                (draftType === "offensive" ? "text-emerald-700" : "text-sky-700")
              }
              data-testid="coaching-point-draft-type"
            >
              {draftType === "offensive" ? "Offensive" : "Defensive"}
            </span>
            <button
              type="button"
              onClick={cancelAdd}
              data-testid="coaching-point-cancel-btn"
              className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800"
            >
              Cancel
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  confirmAdd();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelAdd();
                }
              }}
              placeholder={draftType === "offensive" ? "e.g. Create width early" : "e.g. Press on the trigger"}
              data-testid="coaching-point-text-input"
              className="h-8"
            />
            <button
              type="button"
              onClick={confirmAdd}
              disabled={!draftText.trim()}
              data-testid="coaching-point-confirm-btn"
              aria-label="Add coaching point"
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-slate-900 transition-colors"
            >
              <Check className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {(offensive.length + defensive.length === 0 && mode === null) && (
        <p className="text-[11px] text-slate-400 leading-snug">
          Add offensive or defensive cues that the coach wants to keep in view for this drill.
        </p>
      )}

      {offensive.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="coaching-points-offensive-group">
          <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-emerald-700">
            Offensive
          </span>
          <ul className="flex flex-col gap-1">
            {offensive.map((p) => (
              <CoachingPointRow key={p.id} point={p} onRemove={() => removePoint(p.id)} />
            ))}
          </ul>
        </div>
      )}

      {defensive.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="coaching-points-defensive-group">
          <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-sky-700">
            Defensive
          </span>
          <ul className="flex flex-col gap-1">
            {defensive.map((p) => (
              <CoachingPointRow key={p.id} point={p} onRemove={() => removePoint(p.id)} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------
// Rotations
// ---------------------------------------------------------------
// Free-text swap pairs authored per activity (e.g. { from: "CB",
// to: "CM" }). Displayed in the Info Panel as `[From] ↔ [To]` and
// forwarded to the Session Plan as metadata. Never rendered on the
// pitch canvas itself.
const genRotationId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `rot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Sanitise an "After Set" input to a positive whole number (>=1) or
// null when blank/invalid. Called on every keystroke so users can
// clear the field to unschedule a rotation.
const sanitizeAfterSet = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  // Reject decimals, negatives, non-numerics.
  if (!/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
};

const RotationRow = ({ rotation, onChange, onRemove }) => (
  <li
    className="flex items-center gap-1.5 text-xs text-slate-700"
    data-testid={`rotation-row-${rotation.id}`}
  >
    <input
      type="text"
      value={rotation.from || ""}
      onChange={(e) => onChange({ ...rotation, from: e.target.value })}
      placeholder="From"
      aria-label="Rotation from"
      data-testid={`rotation-from-${rotation.id}`}
      className="min-w-0 flex-1 h-7 rounded border border-slate-200 bg-white px-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 transition-colors"
    />
    <span
      aria-hidden
      className="shrink-0 text-slate-500 font-semibold text-sm select-none"
      title="Bidirectional swap"
    >
      ↔
    </span>
    <input
      type="text"
      value={rotation.to || ""}
      onChange={(e) => onChange({ ...rotation, to: e.target.value })}
      placeholder="To"
      aria-label="Rotation to"
      data-testid={`rotation-to-${rotation.id}`}
      className="min-w-0 flex-1 h-7 rounded border border-slate-200 bg-white px-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 transition-colors"
    />
    {/* Optional "After Set" scheduler — accepts positive whole numbers
        only. Blank field means the rotation is unscheduled and will
        render without a set heading in the Session Plan. */}
    <input
      type="number"
      min="1"
      step="1"
      inputMode="numeric"
      value={rotation.afterSet == null ? "" : rotation.afterSet}
      onChange={(e) => onChange({ ...rotation, afterSet: sanitizeAfterSet(e.target.value) })}
      placeholder="Set"
      aria-label="After set number (optional)"
      title="After set (optional)"
      data-testid={`rotation-after-set-${rotation.id}`}
      className="w-12 shrink-0 h-7 rounded border border-slate-200 bg-white px-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
    <button
      type="button"
      onClick={onRemove}
      data-testid={`rotation-remove-${rotation.id}`}
      aria-label="Remove rotation"
      className="w-6 h-6 shrink-0 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
  </li>
);

const RotationsSection = ({ rotations, onChange }) => {
  const list = rotations || [];
  const addRow = () => {
    onChange([...list, { id: genRotationId(), from: "", to: "", afterSet: null }]);
  };
  const updateRow = (next) => {
    onChange(list.map((r) => (r.id === next.id ? next : r)));
  };
  const removeRow = (id) => {
    onChange(list.filter((r) => r.id !== id));
  };

  return (
    <section className="flex flex-col gap-3" data-testid="rotations-section">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
          Rotations
        </h3>
        <button
          type="button"
          onClick={addRow}
          data-testid="rotation-add-btn"
          aria-label="Add rotation"
          className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">
          No rotations yet. Click + to pair two players, positions or units.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="rotations-list">
          {list.map((rotation) => (
            <RotationRow
              key={rotation.id}
              rotation={rotation}
              onChange={updateRow}
              onRemove={() => removeRow(rotation.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
};




export const InfoPanel = ({
  activity,
  onActivityChange,
  activeTool,
  pitchMode,
  onPitchModeChange,
  showDimensions,
  onShowDimensionsChange,
  fieldColor,
  onFieldColorChange,
  selectedObject,
  selectedShape,
  selectedArrow,
  selectedText,
  selectedLine,
  multiSelectionCount,
  drawStyle,
  onUpdateDrawStyle,
  onUpdateSelectedLabel,
  onUpdateSelectedText,
  onUpdatePlayerDisplayMode,
  onUpdatePlayerDisplayValue,
  onUpdatePlayerColor,
  onUpdatePlayerSize,
  playerColorContext,
  playerDisplayModeContext,
  playerSizeContext,
  onDeleteSelected,
  onBringForward,
  onSendBackward,
  stats,
}) => {
  // Reflect the selected annotation's style in the controls so edits are live.
  const styleValue = selectedShape
    ? { color: selectedShape.color || drawStyle.color, dashed: !!selectedShape.dashed }
    : selectedArrow
      ? { color: selectedArrow.color || drawStyle.color, dashed: !!selectedArrow.dashed }
      : selectedLine
        ? { color: selectedLine.color || drawStyle.color, dashed: !!selectedLine.dashed }
        : drawStyle;

  const styleHeading = selectedShape
    ? "Shape Style"
    : selectedArrow
      ? "Arrow Style"
      : selectedLine
        ? "Line Style"
        : "Draw Style";
  const styleHint = selectedShape || selectedArrow || selectedLine
    ? "Changes apply to the selected item"
    : "Applies to the next arrow, line, rectangle or circle you draw";

  const hasAnySingleSelection = Boolean(
    selectedObject || selectedShape || selectedArrow || selectedText || selectedLine
  );
  const hasAnySelection = hasAnySingleSelection || multiSelectionCount > 0;

  // Style controls only appear when the coach is actively drawing or has a
  // drawable item selected — otherwise the section hides so the panel stays
  // calm.
  const isDrawTool = ["arrow", "line", "rect", "circle"].includes(activeTool);
  const showStyleSection = isDrawTool || selectedShape || selectedArrow || selectedLine;

  // Live-calculated total in minutes: (time + rest) × sets. Empty / negative
  // / non-numeric inputs are treated as zero so nothing invalid ever renders.
  const durNum = (v) => {
    const n = Number(v);
    return isFinite(n) && n > 0 ? n : 0;
  };
  const totalMinutes =
    (durNum(activity.time) + durNum(activity.rest)) * durNum(activity.sets);

  return (
    <aside
      className="bg-white border-l border-slate-200 w-80 shrink-0 overflow-y-auto p-6 flex flex-col gap-7 h-full"
      data-testid="info-panel"
    >
      <div>
        <h2
          className="text-2xl tracking-tight font-bold text-slate-900"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Activity
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Describe the drill and configure the pitch.
        </p>
      </div>

      <Section title="Details">
        <div className="flex flex-col gap-2">
          <Label htmlFor="activity-name" className="text-xs font-semibold text-slate-700">
            Name
          </Label>
          <Input
            id="activity-name"
            data-testid="activity-name-input"
            value={activity.name}
            onChange={(e) => onActivityChange({ ...activity, name: e.target.value })}
            placeholder="e.g. Rondo 4v2"
          />
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="activity-duration-group">
          <div className="flex flex-col gap-2">
            <Label htmlFor="activity-time" className="text-xs font-semibold text-slate-700">
              Time (min)
            </Label>
            <Input
              id="activity-time"
              data-testid="activity-time-input"
              type="number"
              min={0}
              value={activity.time}
              onChange={(e) =>
                onActivityChange({ ...activity, time: e.target.value })
              }
              placeholder="4"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="activity-sets" className="text-xs font-semibold text-slate-700">
              Sets
            </Label>
            <Input
              id="activity-sets"
              data-testid="activity-sets-input"
              type="number"
              min={0}
              step={1}
              value={activity.sets}
              onChange={(e) =>
                onActivityChange({ ...activity, sets: e.target.value })
              }
              placeholder="3"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="activity-rest" className="text-xs font-semibold text-slate-700">
              Rest (min)
            </Label>
            <Input
              id="activity-rest"
              data-testid="activity-rest-input"
              type="number"
              min={0}
              value={activity.rest}
              onChange={(e) =>
                onActivityChange({ ...activity, rest: e.target.value })
              }
              placeholder="1"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="activity-total" className="text-xs font-semibold text-slate-700">
              Total (min)
            </Label>
            <Input
              id="activity-total"
              data-testid="activity-total-value"
              type="text"
              readOnly
              tabIndex={-1}
              aria-readonly="true"
              value={totalMinutes || 0}
              className="bg-slate-50 text-slate-700 font-semibold cursor-default"
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="activity-rules" className="text-xs font-semibold text-slate-700">
            Rules
          </Label>
          <Textarea
            id="activity-rules"
            data-testid="activity-rules-input"
            value={activity.rules || ""}
            onChange={(e) => onActivityChange({ ...activity, rules: e.target.value })}
            placeholder="Rules, conditions, constraints…"
            rows={4}
          />
        </div>
      </Section>

      <CoachingPointsSection
        points={activity.coachingPoints || []}
        onChange={(next) => onActivityChange({ ...activity, coachingPoints: next })}
      />

      <RotationsSection
        rotations={activity.rotations || []}
        onChange={(next) => onActivityChange({ ...activity, rotations: next })}
      />

      <Section title="Pitch">
        <div className="flex flex-col gap-2">
          <Label className="text-xs font-semibold text-slate-700">Orientation</Label>
          <Select value={pitchMode} onValueChange={onPitchModeChange}>
            <SelectTrigger data-testid="pitch-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PITCH_MODES).map(([key, val]) => (
                <SelectItem key={key} value={key} data-testid={`pitch-mode-${key}`}>
                  {val.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label
          className="flex items-center justify-between gap-3 mt-1 cursor-pointer select-none"
          data-testid="show-dimensions-wrap"
        >
          <span className="text-xs font-semibold text-slate-700">Show Dimensions</span>
          <input
            type="checkbox"
            checked={!!showDimensions}
            onChange={(e) => onShowDimensionsChange(e.target.checked)}
            data-testid="show-dimensions-toggle"
            className="h-4 w-4 rounded border-slate-300 accent-slate-900 cursor-pointer"
          />
        </label>
        <div className="flex flex-col gap-1.5 mt-1" data-testid="field-color-control">
          <Label className="text-xs font-semibold text-slate-700">Field Color</Label>
          <div className="flex flex-wrap gap-1.5">
            {FIELD_COLORS.map((c) => {
              const active = (fieldColor || "").toLowerCase() === c.fill.toLowerCase();
              return (
                <button
                  key={c.key}
                  type="button"
                  title={c.label}
                  aria-label={c.label}
                  aria-pressed={active}
                  onClick={() => onFieldColorChange(c.fill)}
                  data-testid={`field-color-${c.key}`}
                  className={
                    "w-6 h-6 rounded-full transition-all border " +
                    (active
                      ? "ring-2 ring-slate-900 ring-offset-2 ring-offset-white border-slate-900"
                      : "border-slate-200 hover:border-slate-400")
                  }
                  style={{ backgroundColor: c.fill }}
                />
              );
            })}
          </div>
        </div>
      </Section>

      {showStyleSection && (
        <Section title={styleHeading}>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3" data-testid="draw-style-panel">
            <p className="text-[11px] text-slate-500">{styleHint}</p>
            <StyleControls value={styleValue} onChange={onUpdateDrawStyle} testIdPrefix="draw-style" />
          </div>
        </Section>
      )}

      {hasAnySelection && (
      <Section title="Selection">
        {selectedObject ? (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-object"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800">
                {OBJECT_TYPES[selectedObject.type].name}
              </span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-slate-400 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Remove object"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="selection-label" className="text-xs font-semibold text-slate-700">
                Label / Number
              </Label>
              <Input
                id="selection-label"
                data-testid="selection-label-input"
                value={selectedObject.label ?? ""}
                onChange={(e) => onUpdateSelectedLabel(e.target.value)}
                placeholder={OBJECT_TYPES[selectedObject.type].shortLabel || "—"}
                maxLength={4}
              />
            </div>
            {isPlayerLikeType(selectedObject.type) && (
              <div className="flex flex-col gap-2" data-testid="player-display-mode-controls">
                <Label className="text-xs font-semibold text-slate-700">Display</Label>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { key: "number", label: "Number" },
                    { key: "initials", label: "Initials" },
                    { key: "position", label: "Position" },
                    { key: "blank", label: "Blank" },
                  ].map((opt) => {
                    const active = (selectedObject.displayMode || "number") === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => onUpdatePlayerDisplayMode(opt.key)}
                        data-testid={`player-display-mode-${opt.key}`}
                        aria-pressed={active}
                        className={
                          "text-[10px] font-semibold uppercase tracking-wide py-1 rounded-md border transition-colors " +
                          (active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100")
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {(selectedObject.displayMode === "initials" || selectedObject.displayMode === "position") && (
                  <Input
                    data-testid="player-display-value-input"
                    value={
                      selectedObject.displayMode === "initials"
                        ? selectedObject.initials ?? ""
                        : selectedObject.position ?? ""
                    }
                    onChange={(e) =>
                      onUpdatePlayerDisplayValue(
                        selectedObject.displayMode,
                        e.target.value.slice(0, 3).toUpperCase()
                      )
                    }
                    placeholder={selectedObject.displayMode === "position" ? "e.g. GK, CAM" : "e.g. JS"}
                    maxLength={3}
                  />
                )}
                <div className="flex flex-col gap-1.5 mt-1" data-testid="player-color-controls">
                  <Label className="text-xs font-semibold text-slate-700">Colour</Label>
                  <PlayerColorSwatches
                    activeColor={
                      selectedObject.color || OBJECT_TYPES[selectedObject.type].fill
                    }
                    onPick={onUpdatePlayerColor}
                    testIdPrefix="player-color"
                  />
                </div>
                <div className="flex flex-col gap-1.5 mt-1" data-testid="player-size-controls">
                  <Label className="text-xs font-semibold text-slate-700">Size</Label>
                  <div
                    className="grid grid-cols-3 gap-1"
                    role="radiogroup"
                    aria-label="Player size"
                  >
                    {PLAYER_SIZE_KEYS.map((key) => {
                      const active = (selectedObject.size || DEFAULT_PLAYER_SIZE) === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => onUpdatePlayerSize(key)}
                          data-testid={`player-size-${key}`}
                          data-active={active ? "true" : "false"}
                          className={
                            "h-8 rounded-md text-xs font-semibold transition-colors border " +
                            (active
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-300 hover:border-slate-400")
                          }
                        >
                          {PLAYER_SIZES[key].label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              {!isPlayerLikeType(selectedObject.type) && (
                <div className="text-[11px] text-slate-500 flex items-center justify-between">
                  <span>Size</span>
                  <span className="tabular-nums font-semibold text-slate-700" data-testid="selection-size-value">
                    {(selectedObject.size ?? 1).toFixed(2)}×
                  </span>
                </div>
              )}
              <div className="text-[11px] text-slate-500 flex items-center justify-between">
                <span>Rotation</span>
                <span className="tabular-nums font-semibold text-slate-700" data-testid="selection-rotation-value">
                  {Math.round(((selectedObject.rotation ?? 0) % 360 + 360) % 360)}°
                </span>
              </div>
              <div className="text-[11px] text-slate-500 flex items-center justify-between">
                <span>Position</span>
                <span className="tabular-nums text-slate-700">
                  x {Math.round(selectedObject.x)}, y {Math.round(selectedObject.y)}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1 leading-snug">
                {isPlayerLikeType(selectedObject.type)
                  ? <>Activate the <span className="font-semibold">Select tool</span> on the left, then drag the green handle above the player to rotate. Use the Size buttons above to resize.</>
                  : <>Activate the <span className="font-semibold">Select tool</span> on the left, then drag the green handles to resize (corner) or rotate (top).</>}
              </p>
            </div>
          </div>
        ) : selectedShape ? (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-shape"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800 capitalize">
                {selectedShape.type}
              </span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-slate-400 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Remove shape"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              Drag anywhere on the shape to move it. Activate the <span className="font-semibold">Select tool</span> on the left, then drag the green corner handle to resize.
            </p>
          </div>
        ) : selectedText ? (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-text"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800">Text label</span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-slate-400 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Remove text"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="text-content" className="text-xs font-semibold text-slate-700">
                Content
              </Label>
              <Input
                id="text-content"
                data-testid="selection-text-content-input"
                value={selectedText.content ?? ""}
                onChange={(e) => onUpdateSelectedText({ content: e.target.value })}
                placeholder="e.g. Attacking third"
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Color</span>
              <div className="flex items-center gap-1.5">
                {COLORS.map((c) => {
                  const active = (selectedText.color || "").toLowerCase() === c.hex.toLowerCase();
                  return (
                    <button
                      type="button"
                      key={c.key}
                      onClick={() => onUpdateSelectedText({ color: c.hex })}
                      data-testid={`text-color-${c.key}`}
                      aria-label={`${c.key} color`}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${
                        active
                          ? "border-slate-900 ring-2 ring-slate-900 ring-offset-1"
                          : "border-slate-300 hover:border-slate-500"
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                  );
                })}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              Drag the label to reposition. Hold <span className="font-semibold">Alt</span> and drag to duplicate.
            </p>
          </div>
        ) : selectedArrow ? (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-arrow"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                <Minus className="w-3.5 h-3.5" /> Arrow
              </span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-slate-400 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Remove arrow"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              Activate the <span className="font-semibold">Select tool</span>, then drag the body to move it, the green endpoints to reshape, or the orange curve handle to bend it.
            </p>
          </div>
        ) : selectedLine ? (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-line"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                <Minus className="w-3.5 h-3.5" /> Line
              </span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-slate-400 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Remove line"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              Toggle Solid / Dashed above. Activate the <span className="font-semibold">Select tool</span> to drag endpoints.
            </p>
          </div>
        ) : multiSelectionCount > 0 ? (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex flex-col gap-3"
            data-testid="selection-editor-multi"
          >            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-emerald-800">
                {multiSelectionCount} item{multiSelectionCount === 1 ? "" : "s"} selected
              </span>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="text-emerald-700 hover:text-red-600 transition-colors"
                data-testid="selection-remove-btn"
                aria-label="Delete selected items"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-emerald-700 leading-snug">
              Drag any highlighted item to move the whole group. Press Delete to remove them all.
            </p>
            {playerDisplayModeContext?.show && (
              <div
                className="flex flex-col gap-2 pt-2 border-t border-emerald-200"
                data-testid="player-display-mode-controls-multi"
              >
                <Label className="text-xs font-semibold text-emerald-900">
                  Display ({playerDisplayModeContext.count} player{playerDisplayModeContext.count === 1 ? "" : "s"})
                </Label>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { key: "number", label: "Number" },
                    { key: "initials", label: "Initials" },
                    { key: "position", label: "Position" },
                    { key: "blank", label: "Blank" },
                  ].map((opt) => {
                    const active = playerDisplayModeContext.activeMode === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => onUpdatePlayerDisplayMode(opt.key)}
                        data-testid={`player-display-mode-multi-${opt.key}`}
                        aria-pressed={active}
                        className={
                          "text-[10px] font-semibold uppercase tracking-wide py-1 rounded-md border transition-colors " +
                          (active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100")
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {playerDisplayModeContext.activeMode === null && (
                  <p className="text-[10px] text-emerald-700 leading-snug">
                    Selected players have mixed display modes — pick one to unify.
                  </p>
                )}
              </div>
            )}
            {playerColorContext?.show && playerColorContext.mode === "multi" && (
              <div className="flex flex-col gap-1.5 pt-2 border-t border-emerald-200" data-testid="player-color-controls-multi">
                <Label className="text-xs font-semibold text-emerald-900">
                  Colour ({playerColorContext.count} player{playerColorContext.count === 1 ? "" : "s"})
                </Label>
                <PlayerColorSwatches
                  activeColor={playerColorContext.activeColor}
                  onPick={onUpdatePlayerColor}
                  testIdPrefix="player-color-multi"
                />
                {playerColorContext.activeColor === null && (
                  <p className="text-[10px] text-emerald-700 leading-snug">
                    Selected players have mixed colours — pick one to unify.
                  </p>
                )}
              </div>
            )}
            {playerSizeContext?.show && playerSizeContext.mode === "multi" && (
              <div
                className="flex flex-col gap-1.5 pt-2 border-t border-emerald-200"
                data-testid="player-size-controls-multi"
              >
                <Label className="text-xs font-semibold text-emerald-900">
                  Size ({playerSizeContext.count} player{playerSizeContext.count === 1 ? "" : "s"})
                </Label>
                <div
                  className="grid grid-cols-3 gap-1"
                  role="radiogroup"
                  aria-label="Player size (multi-selection)"
                >
                  {PLAYER_SIZE_KEYS.map((key) => {
                    const active = playerSizeContext.activeSize === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onUpdatePlayerSize(key)}
                        data-testid={`player-size-multi-${key}`}
                        data-active={active ? "true" : "false"}
                        className={
                          "h-8 rounded-md text-xs font-semibold transition-colors border " +
                          (active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400")
                        }
                      >
                        {PLAYER_SIZES[key].label}
                      </button>
                    );
                  })}
                </div>
                {playerSizeContext.activeSize === null && (
                  <p className="text-[10px] text-emerald-700 leading-snug">
                    Selected players have mixed sizes — pick one to unify.
                  </p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </Section>
      )}

      {(hasAnySingleSelection || multiSelectionCount > 0) && (
        <Section title="Order">
          <div className="flex items-center gap-2" data-testid="order-controls">
            <button
              type="button"
              onClick={onBringForward}
              data-testid="bring-forward-btn"
              title="Bring Forward (Shift+click for Bring to Front)"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-colors"
            >
              <ArrowUpToLine className="w-3.5 h-3.5" />
              Bring Forward
            </button>
            <button
              type="button"
              onClick={onSendBackward}
              data-testid="send-backward-btn"
              title="Send Backward (Shift+click for Send to Back)"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-colors"
            >
              <ArrowDownToLine className="w-3.5 h-3.5" />
              Send Backward
            </button>
          </div>
        </Section>
      )}

      {playerColorContext?.show && playerColorContext.mode === "placement" && (
        <Section title="Placement Colour">
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-4 flex flex-col gap-2"
            data-testid="player-color-controls-placement"
          >
            <p className="text-[11px] text-slate-500 leading-snug">
              Pick a colour before clicking the pitch. Newly placed players will use it.
            </p>
            <PlayerColorSwatches
              activeColor={playerColorContext.activeColor}
              onPick={onUpdatePlayerColor}
              testIdPrefix="player-color-placement"
            />
          </div>
        </Section>
      )}
    </aside>
  );
};

const StatBox = ({ label, value, testId }) => (
  <div className="rounded-md border border-slate-200 bg-white p-3 flex flex-col items-start" data-testid={testId}>
    <span
      className="text-xl font-bold text-slate-900 leading-none"
      style={{ fontFamily: "Manrope, sans-serif" }}
    >
      {value}
    </span>
    <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mt-1">
      {label}
    </span>
  </div>
);
